import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, CheckCircle2, X, RefreshCw, Loader2, AlertTriangle, ShieldAlert, History, ArrowDownToLine, ArrowUpFromLine, Bell, BellOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { useSquareReceipts } from '../../contexts/SquareReceiptsContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

const confidenceTone = (c) => ({
    high:   'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
    medium: 'bg-amber-500/20  text-amber-300  border-amber-500/40',
    low:    'bg-rose-500/20   text-rose-300   border-rose-500/40',
}[c] || 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40');

const ReviewDialog = ({ row, onClose, onDone, onRemoved }) => {
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [reason, setReason] = useState('');
    const [mode, setMode] = useState('approve'); // 'approve' | 'reject'
    const [context, setContext] = useState(null);
    const [loadingCtx, setLoadingCtx] = useState(false);
    const [zoomed, setZoomed] = useState(false);

    // ── Browser-Back button: close the dialog instead of navigating away ───
    // Before this guard, tapping the iOS / Android back button while the
    // receipt review dialog was open popped the entire dashboard route off
    // the stack — which on a fresh session looked exactly like a logout.
    // We push a synthetic history entry on open and listen for popstate to
    // close the dialog cleanly. On manual close we pop the entry back off
    // so we don't pollute the browser history.
    //
    // IMPORTANT: this effect is pinned to row?.id only — NOT the onClose
    // function reference. If we depended on onClose (a fresh arrow function
    // on every parent re-render) the cleanup's `history.back()` would
    // synthesise a popstate event on every poll tick (~every 15s, plus
    // other dashboard re-renders), and the newly-attached listener would
    // catch it and close the dialog underneath the cashier mid-action.
    // That was the "reject dialog vanishes in 1-5 sec" bug.
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
    useEffect(() => {
        if (!row) return;
        const marker = { receiptDialog: true, id: row.id, ts: Date.now() };
        try { window.history.pushState(marker, ''); } catch { /* ignore */ }
        const handlePop = () => { onCloseRef.current?.(); };
        window.addEventListener('popstate', handlePop);
        return () => {
            window.removeEventListener('popstate', handlePop);
            // If our marker is still on top of the stack (i.e. closing was
            // triggered by something OTHER than the Back button), pop it.
            try {
                if (window.history.state && window.history.state.receiptDialog) {
                    window.history.back();
                }
            } catch { /* ignore */ }
        };
    }, [row?.id]);

    useEffect(() => {
        if (row) {
            const a = row.ocr_amount || row.claimed_amount || 0;
            setAmount(a ? Number(a).toFixed(2) : '');
            setNote('');
            setReason('');
            setMode('approve');
            setContext(null);
            setZoomed(false);
            setLoadingCtx(true);
            axios.get(`${API}/cashier/square-receipts/${row.id}/context`, { withCredentials: true })
                .then(({ data }) => setContext(data))
                .catch(() => setContext({ flags: [], recent_loads: [], prior_receipts: [], duplicates: [], player: null }))
                .finally(() => setLoadingCtx(false));
        }
    }, [row]);

    if (!row) return null;
    const amt = parseFloat(amount);

    const approve = async () => {
        if (!amt || amt <= 0) { toast.error('Enter the dollar amount to credit'); return; }
        setSubmitting(true);
        try {
            await axios.post(`${API}/cashier/square-receipts/${row.id}/approve`,
                { amount: amt, payment_method: 'PayPal', note: note.trim() || undefined },
                { withCredentials: true },
            );
            toast.success(`Credited ${fmtMoney(amt)} to ${row.player_name}`);
            // Optimistic: yank the row from the queue immediately so the
            // cashier sees the result before the 15s polling tick catches up.
            onRemoved?.(row.id);
            onDone?.();
            onClose();
        } catch (e) {
            const msg = e.response?.data?.detail || e.message || 'Approval failed';
            toast.error(typeof msg === 'string' ? msg : 'Approval failed');
            console.error('[receipt-approve]', { status: e.response?.status, data: e.response?.data });
        } finally { setSubmitting(false); }
    };

    const reject = async (overrideReason) => {
        const r = (overrideReason || reason || '').trim();
        if (!r) { toast.error('Pick a quick reason or type one — the player needs to know'); return; }
        setSubmitting(true);
        try {
            await axios.post(`${API}/cashier/square-receipts/${row.id}/reject`,
                { reason: r },
                { withCredentials: true },
            );
            toast.success(`Marked rejected — ${row.player_name} notified`);
            // Same optimistic removal — vanish from the queue NOW.
            onRemoved?.(row.id);
            onDone?.();
            onClose();
        } catch (e) {
            const msg = e.response?.data?.detail || e.message || 'Reject failed';
            toast.error(typeof msg === 'string' ? msg : 'Reject failed');
            console.error('[receipt-reject]', { status: e.response?.status, data: e.response?.data });
        } finally { setSubmitting(false); }
    };

    const src = row.image_b64 ? `data:${row.image_mime || 'image/png'};base64,${row.image_b64}` : null;

    return (
        <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="bg-[#0c0c0c] border-[#39FF14]/30 max-w-5xl text-zinc-100 max-h-[92vh] overflow-y-auto" data-testid="receipt-review-dialog">
                <DialogHeader>
                    <DialogTitle className="text-[#39FF14] flex items-center gap-2">
                        <Camera className="w-5 h-5" /> Verify receipt — {row.player_name}
                    </DialogTitle>
                    <DialogDescription className="text-zinc-400">
                        Match the receipt against the OCR&apos;d amount and any note the player left. Approve to credit instantly.
                    </DialogDescription>
                </DialogHeader>

                <RiskFlags loading={loadingCtx} flags={context?.flags || []} />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* COL 1 — Receipt image (click-to-zoom, in-place, never navigates away) */}
                    <div>
                        {src ? (
                            <button
                                type="button"
                                onClick={() => setZoomed((z) => !z)}
                                className="block w-full text-left rounded-lg overflow-hidden border border-white/10 bg-zinc-950 hover:border-[#39FF14]/50 transition-colors"
                                data-testid="receipt-image-zoom"
                                title={zoomed ? 'Tap to shrink' : 'Tap to zoom'}
                            >
                                <img
                                    src={src}
                                    alt="Square receipt"
                                    className={`w-full transition-all ${zoomed ? 'max-h-[80vh] object-contain' : 'max-h-[420px] object-contain'}`}
                                />
                            </button>
                        ) : <div className="text-xs text-zinc-500">No image</div>}
                    </div>

                    {/* COL 2 — OCR fields + approve / reject form */}
                    <div className="space-y-2 text-xs">
                        <Field label="OCR amount"      value={fmtMoney(row.ocr_amount)} />
                        <Field label="Player claimed"  value={row.claimed_amount ? fmtMoney(row.claimed_amount) : '—'} />
                        <Field label="OCR confidence"  value={
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase border ${confidenceTone(row.ocr_confidence)}`}>
                                {row.ocr_confidence}
                            </span>
                        } />
                        <Field label="Last 4"     value={row.ocr_last4 || '—'} />
                        <Field label="Merchant"   value={row.ocr_merchant || '—'} />
                        <Field label="Receipt at" value={fmtTime(row.ocr_timestamp_iso)} />
                        <Field label="Sent at"    value={fmtTime(row.created_at)} />
                        {row.note && <Field label="Player note" value={row.note} />}
                        {row.raw_text && (
                            <details className="pt-2">
                                <summary className="cursor-pointer text-[10px] uppercase tracking-widest text-zinc-500">Raw OCR text</summary>
                                <pre className="mt-1 p-2 rounded bg-zinc-900 border border-zinc-700 text-[10px] text-zinc-300 whitespace-pre-wrap max-h-32 overflow-auto">{row.raw_text}</pre>
                            </details>
                        )}

                        <div className="pt-4 border-t border-white/10 space-y-2">
                            {mode === 'approve' ? (
                                <>
                                    <div>
                                        <Label className="text-zinc-300">Amount to credit</Label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#39FF14] font-bold">$</span>
                                            <Input type="number" step="0.01" min="0.01"
                                                value={amount} onChange={(e) => setAmount(e.target.value)}
                                                className="bg-zinc-950 border-zinc-700 text-zinc-100 pl-7 font-mono text-base"
                                                data-testid="approve-amount"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-zinc-300">Note (optional)</Label>
                                        <Input value={note} onChange={(e) => setNote(e.target.value)}
                                            maxLength={120}
                                            placeholder="optional"
                                            className="bg-zinc-950 border-zinc-700 text-zinc-100"
                                            data-testid="approve-note"
                                        />
                                    </div>
                                </>
                            ) : (
                                <div>
                                    <Label className="text-zinc-300">Reason (player will see this)</Label>
                                    {/* Quick-pick chips ABOVE the input so accidental
                                        mis-taps near the input field don't fire one of them.
                                        They populate the field — they DON'T auto-submit. The
                                        cashier still has to hit the big red "Reject receipt"
                                        button below. Larger touch targets (min-h-9) for safety. */}
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                        {[
                                            'Duplicate receipt',
                                            'Amount unclear / blurry',
                                            'Amount doesn\u2019t match PayPal',
                                            'Wrong recipient',
                                            'Not a PayPal receipt',
                                            'Old / not from today',
                                        ].map((r) => (
                                            <button
                                                key={r}
                                                type="button"
                                                onClick={() => setReason(r)}
                                                disabled={submitting}
                                                className={`min-h-9 text-[11px] px-2.5 py-1.5 rounded border transition-colors disabled:opacity-50 ${
                                                    reason === r
                                                        ? 'bg-rose-500/30 border-rose-400 text-rose-100 font-bold'
                                                        : 'bg-rose-500/10 border-rose-500/40 text-rose-200 hover:bg-rose-500/20'
                                                }`}
                                                data-testid={`reject-quick-${r.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                                            >
                                                {r}
                                            </button>
                                        ))}
                                    </div>
                                    <Input value={reason} onChange={(e) => setReason(e.target.value)}
                                        maxLength={200}
                                        placeholder="Tap a quick reason above, or type your own"
                                        className="bg-zinc-950 border-zinc-700 text-zinc-100"
                                        data-testid="reject-reason"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* COL 3 — Player history & context */}
                    <PlayerHistoryPanel loading={loadingCtx} ctx={context} />
                </div>

                <DialogFooter className="flex flex-wrap gap-2 items-center justify-between">
                    {mode === 'approve' ? (
                        <>
                            <Button variant="ghost" onClick={() => setMode('reject')} className="text-rose-300 hover:bg-rose-500/10" data-testid="switch-to-reject">
                                <AlertTriangle className="w-4 h-4 mr-1" /> Reject instead
                            </Button>
                            <Button onClick={() => approve()} disabled={!amt || submitting}
                                className="bg-[#39FF14] hover:bg-[#5cff3a] text-zinc-900 font-black"
                                data-testid="approve-credit">
                                {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Crediting…</>
                                            : <><CheckCircle2 className="w-4 h-4 mr-1" /> Credit {amount ? fmtMoney(amt) : ''}</>}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="ghost" onClick={() => setMode('approve')} className="text-emerald-300 hover:bg-emerald-500/10" data-testid="switch-to-approve">
                                ← Approve instead
                            </Button>
                            <Button onClick={() => reject()} disabled={!reason.trim() || submitting}
                                className="bg-rose-500 hover:bg-rose-400 text-white font-black"
                                data-testid="reject-confirm">
                                {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Rejecting…</>
                                            : <><X className="w-4 h-4 mr-1" /> Reject receipt</>}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const Field = ({ label, value }) => (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-white/5">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
        <span className="text-zinc-100 font-mono text-xs text-right">{value}</span>
    </div>
);

// ─── Risk flag banner ────────────────────────────────────────────────────
const FLAG_TONE = {
    high:   'bg-rose-500/15   border-rose-500/40   text-rose-200',
    medium: 'bg-amber-500/15  border-amber-500/40  text-amber-200',
    low:    'bg-zinc-500/15   border-zinc-500/40   text-zinc-200',
};

const RiskFlags = ({ flags, loading }) => {
    if (loading) {
        return (
            <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Pulling player history…
            </div>
        );
    }
    if (!flags || flags.length === 0) {
        return (
            <div className="flex items-center gap-2 text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-2.5 py-1.5">
                <ShieldAlert className="w-3.5 h-3.5" /> No risk signals on this player — clean approve.
            </div>
        );
    }
    return (
        <div className="space-y-1.5" data-testid="receipt-risk-flags">
            {flags.map((f) => (
                <div key={f.code}
                    className={`flex items-center gap-2 text-[11px] px-2.5 py-1.5 rounded border ${FLAG_TONE[f.level] || FLAG_TONE.low}`}
                    data-testid={`risk-flag-${f.code}`}>
                    <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
                    <span className="font-bold uppercase tracking-wider text-[9px]">{f.level}</span>
                    <span>{f.msg}</span>
                </div>
            ))}
        </div>
    );
};

// ─── Player history panel ────────────────────────────────────────────────
const PlayerHistoryPanel = ({ ctx, loading }) => {
    if (loading || !ctx) {
        return (
            <div className="rounded-lg border border-white/10 bg-zinc-950 p-3 text-[11px] text-zinc-500 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading history…
            </div>
        );
    }
    const p = ctx.player || {};
    return (
        <div className="space-y-3 text-xs" data-testid="receipt-player-history">
            <div className="rounded-lg border border-white/10 bg-zinc-950 p-3 space-y-1.5">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                    <History className="w-3 h-3" /> Player snapshot
                </div>
                <Field label="Joined"   value={`${p.days_old != null ? `${p.days_old}d ago` : '—'}`} />
                <Field label="Balance"  value={fmtMoney(p.credits)} />
                <Field label="Lifetime loaded" value={fmtMoney(p.lifetime_loaded)} />
                <Field label="Lifetime cashed" value={fmtMoney(p.lifetime_cashed)} />
                <Field label="Receipts last 24h" value={ctx.velocity_24h} />
                {ctx.pending_count > 0 && (
                    <Field label="Other pending" value={ctx.pending_count} />
                )}
            </div>

            {ctx.duplicates && ctx.duplicates.length > 0 && (
                <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 space-y-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-rose-300 flex items-center gap-1.5 font-bold">
                        <AlertTriangle className="w-3 h-3" /> Duplicate image · {ctx.duplicates.length}
                    </div>
                    {ctx.duplicates.map((d) => (
                        <div key={d.id} className="flex items-center justify-between text-[11px]">
                            <span className="text-zinc-200 truncate">{d.player_name}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase border ${
                                d.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
                                d.status === 'rejected' ? 'bg-rose-500/20 text-rose-300 border-rose-500/40' :
                                                          'bg-amber-500/20 text-amber-300 border-amber-500/40'
                            }`}>{d.status}</span>
                            <span className="font-mono text-zinc-300">{fmtMoney(d.ocr_amount)}</span>
                            <span className="text-zinc-500">{fmtTime(d.created_at)}</span>
                        </div>
                    ))}
                </div>
            )}

            {ctx.recent_loads && ctx.recent_loads.length > 0 && (
                <div className="rounded-lg border border-white/10 bg-zinc-950 p-3 space-y-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                        <ArrowDownToLine className="w-3 h-3" /> Recent loads
                    </div>
                    {ctx.recent_loads.map((l, i) => (
                        <div key={i} className="flex items-center justify-between text-[11px]">
                            <span className="font-mono text-emerald-300">+{fmtMoney(l.amount)}</span>
                            <span className="text-zinc-400 truncate flex-1 mx-2">{l.method || l.type}</span>
                            <span className="text-zinc-500">{fmtTime(l.at)}</span>
                        </div>
                    ))}
                </div>
            )}

            {ctx.recent_cashouts && ctx.recent_cashouts.length > 0 && (
                <div className="rounded-lg border border-white/10 bg-zinc-950 p-3 space-y-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                        <ArrowUpFromLine className="w-3 h-3" /> Recent cashouts
                    </div>
                    {ctx.recent_cashouts.map((c, i) => (
                        <div key={i} className="flex items-center justify-between text-[11px]">
                            <span className="font-mono text-rose-300">−{fmtMoney(c.amount)}</span>
                            <span className="text-zinc-500">{fmtTime(c.at)}</span>
                        </div>
                    ))}
                </div>
            )}

            {ctx.prior_receipts && ctx.prior_receipts.length > 0 && (
                <div className="rounded-lg border border-white/10 bg-zinc-950 p-3 space-y-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                        <Camera className="w-3 h-3" /> Prior receipts · {ctx.prior_receipts.length}
                    </div>
                    {ctx.prior_receipts.map((r) => (
                        <div key={r.id} className="flex items-center justify-between text-[11px]">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase border ${
                                r.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
                                r.status === 'rejected' ? 'bg-rose-500/20 text-rose-300 border-rose-500/40' :
                                                          'bg-amber-500/20 text-amber-300 border-amber-500/40'
                            }`}>{r.status}</span>
                            <span className="font-mono text-zinc-300">{fmtMoney(r.credit_amount || r.ocr_amount)}</span>
                            <span className="text-zinc-500">{fmtTime(r.created_at)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Cashier / Admin facing card — lists pending Square receipts. One-click open
 * opens the side-by-side review dialog above.
 */
const SquareReceiptsQueueCard = () => {
    const { rows, loading, flash, muted, refresh, toggleMute,
            pendingOpen, clearOpenReview, optimisticRemove } = useSquareReceipts();
    const [active, setActive] = useState(null);

    // Auto-open the review dialog when something (toast / floating badge /
    // staff alert "View") asks to open a specific receipt. We depend on the
    // whole {id, nonce} object so RE-opening the SAME receipt right after
    // closing also triggers this effect (otherwise React skips because the
    // id-only value didn't change).
    useEffect(() => {
        if (!pendingOpen?.id) return;
        const match = rows.find((r) => String(r.id) === String(pendingOpen.id));
        if (match) {
            setActive(match);
            clearOpenReview();
        }
    }, [pendingOpen, rows, clearOpenReview]);

    return (
        <div id="square-receipts"
            className={`card-dark p-4 transition-colors ${flash ? 'square-receipts-flash' : ''}`}
            data-testid="square-receipts-queue">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                    <h3 className="text-white text-base font-black flex items-center gap-2">
                        <Camera className="w-4 h-4 text-[#39FF14]" />
                        Payment Receipts — pending review
                        {rows.length > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-[#39FF14]/20 border border-[#39FF14]/40 text-[#39FF14]">
                                {rows.length}
                            </span>
                        )}
                    </h3>
                    <p className="text-[10px] text-[#A0AAB2] mt-0.5">
                        Auto-refreshing every 15s · OCR pre-fills the amount · 1-click to credit
                    </p>
                </div>
                <div className="flex items-center gap-1.5">
                    <button onClick={toggleMute}
                        className={`p-1.5 rounded-lg border ${muted ? 'border-rose-500/40 text-rose-300 bg-rose-500/10' : 'border-white/10 text-[#A0AAB2] hover:bg-white/5'}`}
                        title={muted ? 'Chime muted — click to unmute' : 'Chime on — click to mute'}
                        data-testid="square-receipts-mute-toggle"
                    >
                        {muted ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                    </button>
                    <button onClick={() => refresh()} disabled={loading}
                        className="p-1.5 rounded-lg border border-white/10 hover:bg-white/5"
                        data-testid="square-receipts-refresh">
                        <RefreshCw className={`w-4 h-4 text-[#A0AAB2] ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {loading && rows.length === 0 ? (
                <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-5 h-5 text-[#39FF14] animate-spin" />
                </div>
            ) : rows.length === 0 ? (
                <p className="text-center text-[#A0AAB2] py-6 text-sm">No receipts waiting — you&apos;re caught up ✨</p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {rows.map((r) => {
                        const src = r.image_b64 ? `data:${r.image_mime || 'image/png'};base64,${r.image_b64}` : null;
                        return (
                            <button
                                key={r.id}
                                onClick={() => setActive(r)}
                                className="text-left rounded-xl bg-zinc-950 border border-white/10 hover:border-[#39FF14]/60 transition-colors overflow-hidden flex flex-col"
                                data-testid={`receipt-row-${r.id}`}
                            >
                                {src && (
                                    <img src={src} alt="receipt thumbnail"
                                        className="w-full h-32 object-cover border-b border-white/5" />
                                )}
                                <div className="p-3 flex-1 flex flex-col gap-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-bold text-white truncate">{r.player_name}</span>
                                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase border ${confidenceTone(r.ocr_confidence)}`}>
                                            {r.ocr_confidence}
                                        </span>
                                    </div>
                                    {r.duplicate_txn_review_pending && (
                                        <div
                                            data-testid={`receipt-dup-review-${r.id}`}
                                            className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded bg-rose-500/20 border border-rose-500/50 text-rose-200"
                                            title="Same transaction ID seen on another receipt — review before crediting"
                                        >
                                            <AlertTriangle className="w-3 h-3" />
                                            Duplicate — Review
                                        </div>
                                    )}
                                    <div className="text-2xl font-black font-mono text-[#39FF14]">
                                        {fmtMoney(r.ocr_amount || r.claimed_amount || 0)}
                                    </div>
                                    <div className="text-[10px] text-zinc-500">
                                        {fmtTime(r.created_at)}
                                        {r.ocr_last4 && ` · •••• ${r.ocr_last4}`}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            <ReviewDialog row={active} onClose={() => setActive(null)} onDone={refresh} onRemoved={optimisticRemove} />
        </div>
    );
};

export default SquareReceiptsQueueCard;
